import React, { useState } from "react";
import { Box, Typography, IconButton, Modal, Backdrop, Fade } from "@mui/material";
import {
    PlayCircleOutline,
    GetApp as DownloadIcon,
    InsertDriveFile as FileIcon,
    PictureAsPdf as PdfIcon,
    Close as CloseIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    ArrowBackIos,
    ArrowForwardIos,
    Fullscreen,
    FullscreenExit,
} from "@mui/icons-material";
import styles from "./MediaGrid.module.scss";
import { MediaType } from "../../../constants/mediaConstants";

/**
 * Interface for media items that can be displayed in the grid
 * This supports all media types defined in our media constants
 */
interface MediaItem {
    mediaType: string;
    mediaUrl: string;
    thumbnailUrl?: string;
    fileName?: string;
    fileSize?: number;
    duration?: string;
}

/**
 * Props for the MediaGrid component
 */
interface MediaGridProps {
    media: MediaItem[];
    maxDisplayItems?: number; // Maximum number of items to show in grid
    className?: string;
    onMediaClick?: (media: MediaItem, index: number) => void; // Parent callback for additional handling
}

/**
 * MediaGrid Component
 *
 * A unified component for displaying mixed media types in a responsive grid.
 * Supports images, videos, PDFs, documents, and GIFs with appropriate previews
 * and interaction handling.
 *
 * Features:
 * - Responsive grid layout that adapts to content
 * - Click to view/download functionality
 * - Modal viewing for images and videos
 * - Proper preview thumbnails for all media types
 * - Download capability for documents and PDFs
 * - Hover effects and accessibility support
 */
const MediaGrid: React.FC<MediaGridProps> = ({
    media,
    maxDisplayItems = 4,
    className = "",
    onMediaClick,
}) => {
    // State for modal viewing
    const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
    const [imageZoom, setImageZoom] = useState(1);
    const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [isLoading, setIsLoading] = useState(false);

    /**
     * Determines if a media type should be viewable in modal
     * Images, videos, and GIFs can be viewed in modal
     */
    const isViewableInModal = (mediaType: string): boolean => {
        return ["image", "video", "gif"].includes(mediaType.toLowerCase());
    };

    /**
     * Handles media item click - either opens modal or triggers download
     */
    const handleMediaClick = (event: React.MouseEvent, media: MediaItem, index: number) => {
        // Prevent event bubbling to parent containers (like CardContent in Post component)
        event.stopPropagation();
        event.preventDefault();

        // Call parent callback if provided
        if (onMediaClick) {
            onMediaClick(media, index);
        }

        // Handle different media types
        if (isViewableInModal(media.mediaType)) {
            setCurrentMediaIndex(index);
            setSelectedMedia(media);
            setImageZoom(1);
            setImagePosition({ x: 0, y: 0 });
            setIsLoading(true);
            setIsModalOpen(true);
        } else {
            // For PDFs and documents, open in new tab or download
            window.open(media.mediaUrl, "_blank");
        }
    };

    /**
     * Closes the modal viewer and prevents event bubbling
     */
    const handleCloseModal = (event?: any, reason?: string) => {
        // Prevent event bubbling if there's an event
        if (event && typeof event.stopPropagation === "function") {
            event.stopPropagation();
            event.preventDefault();
        }

        setIsModalOpen(false);
        setSelectedMedia(null);
        setCurrentMediaIndex(0);
        setImageZoom(1);
        setImagePosition({ x: 0, y: 0 });
        setIsLoading(false);
    };

    /**
     * Navigate to the next media item
     */
    const handleNextMedia = (event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation();
        }

        const viewableMedia = media.filter((item) => isViewableInModal(item.mediaType));
        const currentViewableIndex = viewableMedia.findIndex((_, idx) => {
            const originalIndex = media.findIndex((m) => m === viewableMedia[idx]);
            return originalIndex === currentMediaIndex;
        });

        if (currentViewableIndex < viewableMedia.length - 1) {
            const nextViewableIndex = currentViewableIndex + 1;
            const nextOriginalIndex = media.findIndex(
                (m) => m === viewableMedia[nextViewableIndex],
            );
            setCurrentMediaIndex(nextOriginalIndex);
            setSelectedMedia(viewableMedia[nextViewableIndex]);
            setImageZoom(1);
            setImagePosition({ x: 0, y: 0 });
            setIsLoading(true);
        }
    };

    /**
     * Navigate to the previous media item
     */
    const handlePrevMedia = (event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation();
        }

        const viewableMedia = media.filter((item) => isViewableInModal(item.mediaType));
        const currentViewableIndex = viewableMedia.findIndex((_, idx) => {
            const originalIndex = media.findIndex((m) => m === viewableMedia[idx]);
            return originalIndex === currentMediaIndex;
        });

        if (currentViewableIndex > 0) {
            const prevViewableIndex = currentViewableIndex - 1;
            const prevOriginalIndex = media.findIndex(
                (m) => m === viewableMedia[prevViewableIndex],
            );
            setCurrentMediaIndex(prevOriginalIndex);
            setSelectedMedia(viewableMedia[prevViewableIndex]);
            setImageZoom(1);
            setImagePosition({ x: 0, y: 0 });
            setIsLoading(true);
        }
    };

    /**
     * Handle zoom functionality for images
     */
    const handleZoom = (event: React.WheelEvent, direction?: "in" | "out") => {
        event.preventDefault();
        event.stopPropagation();

        const zoomFactor = 0.2;
        const maxZoom = 3;
        const minZoom = 0.5;

        let newZoom = imageZoom;

        if (direction) {
            newZoom = direction === "in" ? imageZoom + zoomFactor : imageZoom - zoomFactor;
        } else {
            newZoom = event.deltaY < 0 ? imageZoom + zoomFactor : imageZoom - zoomFactor;
        }

        newZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));
        setImageZoom(newZoom);

        // Reset position if zooming out to 1x
        if (newZoom <= 1) {
            setImagePosition({ x: 0, y: 0 });
        }
    };

    /**
     * Handle image dragging when zoomed
     */
    const handleMouseDown = (event: React.MouseEvent) => {
        if (imageZoom > 1) {
            event.preventDefault();
            setIsDragging(true);
            setDragStart({
                x: event.clientX - imagePosition.x,
                y: event.clientY - imagePosition.y,
            });
        }
    };

    const handleMouseMove = (event: React.MouseEvent) => {
        if (isDragging && imageZoom > 1) {
            event.preventDefault();
            setImagePosition({
                x: event.clientX - dragStart.x,
                y: event.clientY - dragStart.y,
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    /**
     * Keyboard navigation
     */
    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!isModalOpen) return;

            switch (event.key) {
                case "Escape":
                    handleCloseModal();
                    break;
                case "ArrowLeft":
                    handlePrevMedia();
                    break;
                case "ArrowRight":
                    handleNextMedia();
                    break;
                case "+":
                case "=":
                    handleZoom(event as any, "in");
                    break;
                case "-":
                    handleZoom(event as any, "out");
                    break;
            }
        };

        if (isModalOpen) {
            document.addEventListener("keydown", handleKeyDown);
            return () => document.removeEventListener("keydown", handleKeyDown);
        }
    }, [isModalOpen, currentMediaIndex, imageZoom]);

    /**
     * Get viewable media for navigation
     */
    const getViewableMedia = () => {
        return media.filter((item) => isViewableInModal(item.mediaType));
    };

    const getCurrentMediaPosition = () => {
        const viewableMedia = getViewableMedia();
        const currentViewableIndex = viewableMedia.findIndex((_, idx) => {
            const originalIndex = media.findIndex((m) => m === viewableMedia[idx]);
            return originalIndex === currentMediaIndex;
        });
        return {
            current: currentViewableIndex + 1,
            total: viewableMedia.length,
        };
    };

    /**
     * Gets the appropriate icon for a media type
     */
    const getMediaIcon = (mediaType: string) => {
        switch (mediaType.toLowerCase()) {
            case "pdf":
                return <PdfIcon sx={{ fontSize: "2rem", color: "#d32f2f" }} />;
            case "document":
            case "file":
                return <FileIcon sx={{ fontSize: "2rem", color: "#1976d2" }} />;
            default:
                return <FileIcon sx={{ fontSize: "2rem", color: "#757575" }} />;
        }
    };

    /**
     * Renders a single media item in the grid
     */
    const renderMediaItem = (item: MediaItem, index: number) => {
        const isImage = ["image", "gif"].includes(item.mediaType.toLowerCase());
        const isVideo = item.mediaType.toLowerCase() === "video";
        const isDocument = ["pdf", "document", "file"].includes(item.mediaType.toLowerCase());

        return (
            <Box
                key={index}
                className={`${styles.mediaItem} ${styles[`mediaItem--${item.mediaType.toLowerCase()}`]}`}
                onClick={(event) => handleMediaClick(event, item, index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        e.preventDefault();
                        handleMediaClick(e as any, item, index);
                    }
                }}
                aria-label={`View ${item.mediaType} ${index + 1}`}
            >
                {/* Image and GIF display */}
                {isImage && (
                    <>
                        <img
                            src={item.thumbnailUrl || item.mediaUrl}
                            alt={`Media ${index + 1}`}
                            className={styles.mediaImage}
                            loading="lazy"
                        />
                        <div className={styles.overlay}>
                            <ZoomInIcon className={styles.overlayIcon} />
                        </div>
                    </>
                )}

                {/* Video display */}
                {isVideo && (
                    <div className={styles.videoContainer}>
                        <video
                            src={item.mediaUrl}
                            className={styles.mediaVideo}
                            poster={item.thumbnailUrl}
                            muted
                            playsInline
                            onMouseOver={(e) => e.currentTarget.play()}
                            onMouseOut={(e) => e.currentTarget.pause()}
                            loop
                        />
                        <div className={styles.overlay}>
                            <PlayCircleOutline className={styles.playIcon} />
                        </div>
                        {item.duration && (
                            <Typography className={styles.videoDuration}>
                                {item.duration}
                            </Typography>
                        )}
                    </div>
                )}

                {/* Document and PDF display */}
                {isDocument && (
                    <div className={styles.documentContainer}>
                        <div className={styles.documentIcon}>{getMediaIcon(item.mediaType)}</div>
                        <Typography variant="caption" className={styles.fileName} noWrap>
                            {item.fileName || `${item.mediaType.toUpperCase()} file`}
                        </Typography>
                        <div className={styles.overlay}>
                            <DownloadIcon className={styles.downloadIcon} />
                            <span>Download</span>
                        </div>
                    </div>
                )}

                {/* Show +N overlay for additional items */}
                {index === maxDisplayItems - 1 && media.length > maxDisplayItems && (
                    <Box className={styles.moreItemsOverlay}>
                        <Typography variant="h6" className={styles.moreItemsText}>
                            +{media.length - maxDisplayItems}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    /**
     * Gets grid CSS class based on number of items
     */
    const getGridClass = () => {
        const itemCount = Math.min(media.length, maxDisplayItems);
        if (itemCount === 1) return styles.gridSingle;
        if (itemCount === 2) return styles.gridDouble;
        if (itemCount === 3) return styles.gridTriple;
        return styles.gridQuad;
    };

    // Don't render if no media
    if (!media || media.length === 0) {
        return null;
    }

    return (
        <>
            <Box className={`${styles.mediaGrid} ${getGridClass()} ${className}`}>
                {media.slice(0, maxDisplayItems).map((item, index) => renderMediaItem(item, index))}
            </Box>

            {/* Enhanced Professional Media Viewer Modal */}
            <Modal
                open={isModalOpen}
                onClose={handleCloseModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 300,
                    onClick: (event: React.MouseEvent) => {
                        event.stopPropagation();
                        event.preventDefault();
                        handleCloseModal(event);
                    },
                }}
                className={styles.modal}
            >
                <Fade in={isModalOpen}>
                    <Box
                        className={styles.modalContainer}
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                        onMouseMove={handleMouseMove}
                        onMouseUp={handleMouseUp}
                        onMouseLeave={handleMouseUp}
                    >
                        {/* Top Control Bar */}
                        <Box className={styles.topControls}>
                            <Box className={styles.mediaCounter}>
                                <Typography variant="body2" className={styles.counterText}>
                                    {getCurrentMediaPosition().current} of{" "}
                                    {getCurrentMediaPosition().total}
                                </Typography>
                            </Box>

                            <Box className={styles.topActions}>
                                {/* Zoom Controls for Images */}
                                {selectedMedia &&
                                    ["image", "gif"].includes(
                                        selectedMedia.mediaType.toLowerCase(),
                                    ) && (
                                        <>
                                            <IconButton
                                                className={styles.controlButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleZoom(e as any, "out");
                                                }}
                                                disabled={imageZoom <= 0.5}
                                                aria-label="Zoom out"
                                            >
                                                <ZoomOutIcon />
                                            </IconButton>

                                            <Typography
                                                variant="body2"
                                                className={styles.zoomIndicator}
                                            >
                                                {Math.round(imageZoom * 100)}%
                                            </Typography>

                                            <IconButton
                                                className={styles.controlButton}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleZoom(e as any, "in");
                                                }}
                                                disabled={imageZoom >= 3}
                                                aria-label="Zoom in"
                                            >
                                                <ZoomInIcon />
                                            </IconButton>
                                        </>
                                    )}

                                {/* Close Button */}
                                <IconButton
                                    className={`${styles.controlButton} ${styles.closeButton}`}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleCloseModal(event);
                                    }}
                                    aria-label="Close media view"
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Main Media Display Area */}
                        <Box className={styles.mediaDisplayArea}>
                            {/* Navigation Arrows */}
                            {getViewableMedia().length > 1 && (
                                <>
                                    <IconButton
                                        className={`${styles.navButton} ${styles.navButtonLeft}`}
                                        onClick={handlePrevMedia}
                                        disabled={getCurrentMediaPosition().current === 1}
                                        aria-label="Previous media"
                                    >
                                        <ArrowBackIos />
                                    </IconButton>

                                    <IconButton
                                        className={`${styles.navButton} ${styles.navButtonRight}`}
                                        onClick={handleNextMedia}
                                        disabled={
                                            getCurrentMediaPosition().current ===
                                            getCurrentMediaPosition().total
                                        }
                                        aria-label="Next media"
                                    >
                                        <ArrowForwardIos />
                                    </IconButton>
                                </>
                            )}

                            {/* Media Content */}
                            <Box className={styles.mediaContent}>
                                {isLoading && (
                                    <Box className={styles.loadingContainer}>
                                        <div className={styles.loadingSpinner}></div>
                                        <Typography variant="body2" className={styles.loadingText}>
                                            Loading...
                                        </Typography>
                                    </Box>
                                )}

                                {/* Image Display */}
                                {selectedMedia &&
                                    ["image", "gif"].includes(
                                        selectedMedia.mediaType.toLowerCase(),
                                    ) && (
                                        <img
                                            src={selectedMedia.mediaUrl}
                                            alt="Enlarged media"
                                            className={styles.modalImage}
                                            style={{
                                                transform: `scale(${imageZoom}) translate(${imagePosition.x / imageZoom}px, ${imagePosition.y / imageZoom}px)`,
                                                cursor:
                                                    imageZoom > 1
                                                        ? isDragging
                                                            ? "grabbing"
                                                            : "grab"
                                                        : "default",
                                                transition: isDragging
                                                    ? "none"
                                                    : "transform 0.2s ease",
                                            }}
                                            onMouseDown={handleMouseDown}
                                            onWheel={handleZoom}
                                            onLoad={() => setIsLoading(false)}
                                            onError={() => setIsLoading(false)}
                                            draggable={false}
                                        />
                                    )}

                                {/* Video Display */}
                                {selectedMedia &&
                                    selectedMedia.mediaType.toLowerCase() === "video" && (
                                        <video
                                            src={selectedMedia.mediaUrl}
                                            className={styles.modalVideo}
                                            controls
                                            autoPlay
                                            loop
                                            onLoadStart={() => setIsLoading(true)}
                                            onCanPlay={() => setIsLoading(false)}
                                            onError={() => setIsLoading(false)}
                                        />
                                    )}
                            </Box>
                        </Box>

                        {/* Bottom Info Bar */}
                        {selectedMedia && (
                            <Box className={styles.bottomInfo}>
                                <Typography variant="body2" className={styles.mediaTitle}>
                                    {selectedMedia.fileName ||
                                        `${selectedMedia.mediaType.toUpperCase()} ${getCurrentMediaPosition().current}`}
                                </Typography>

                                <Box className={styles.bottomActions}>
                                    <Typography variant="caption" className={styles.helpText}>
                                        {selectedMedia.mediaType === "image" ||
                                        selectedMedia.mediaType === "gif"
                                            ? "Scroll to zoom • Drag to pan • ← → to navigate"
                                            : "← → to navigate • ESC to close"}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Fade>
            </Modal>
        </>
    );
};

export default MediaGrid;
